/**
 * Page publique atteinte en scannant le QR code d'une agence : le nouveau salarié
 * saisit l'essentiel, sa fiche est créée, puis il enchaîne sur le parcours de complétion.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, ChevronRight, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_OPTIONS = [
  { value: 'VRP', label: 'Commercial VRP (salarié)' },
  { value: 'VDI', label: 'Commercial VDI' },
  { value: 'AE', label: "Apporteur d'affaires (auto-entrepreneur)" },
  { value: 'Externe', label: 'Externe (sans contrat)' },
];

const PARTNER_OPTIONS = [
  { value: 'octopus', label: 'Octopus Energy' },
  { value: 'ilek', label: 'ilek' },
  { value: 'bouygues', label: 'Bouygues Telecom' },
];

const inputCls = "w-full min-w-0 max-w-full box-border border border-gray-200 rounded-xl px-3 sm:px-4 py-3 text-base sm:text-sm bg-white text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 outline-none";

export default function AgencySignup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [agence, setAgence] = useState('');
  const [statuts, setStatuts] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    civilite: '', nom: '', prenom: '', email: '', telephone: '', statut_contrat: '',
    partenaires: [],
  });

  const togglePartner = (value) => setForm(f => ({
    ...f,
    partenaires: f.partenaires.includes(value)
      ? f.partenaires.filter(p => p !== value)
      : [...f.partenaires, value],
  }));

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/agency-signup/${token}`);
        if (!res.ok) { setStatus('invalid'); return; }
        const data = await res.json();
        setAgence(data.agence);
        if (Array.isArray(data.statuts) && data.statuts.length) {
          setStatuts(data.statuts);
          if (data.statuts.length === 1) setForm(f => ({ ...f, statut_contrat: data.statuts[0] }));
        }
        setStatus('ready');
      } catch {
        setStatus('invalid');
      }
    };
    load();
  }, [token]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.civilite || !form.nom.trim() || !form.prenom.trim() || !form.email.trim() || !form.statut_contrat) {
      setError('Merci de renseigner tous les champs obligatoires.');
      return;
    }
    if (!form.partenaires.length) {
      setError('Indiquez au moins un partenaire avec lequel vous allez travailler.');
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch(`${API}/api/agency-signup/${token}/register?base_url=${encodeURIComponent(window.location.origin)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        let detail = "Une erreur est survenue. Réessayez.";
        try { detail = (await res.json()).detail || detail; } catch { /* réponse non JSON */ }
        setError(detail);
        setStatus('ready');
        return;
      }
      const data = await res.json();
      navigate(`/completion/${data.completion_token}`, { replace: true });
    } catch {
      setError('Erreur réseau. Réessayez.');
      setStatus('ready');
    }
  };

  if (status === 'loading') return (
    <div className="force-light min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="animate-spin text-blue-600" size={32} />
    </div>
  );

  if (status === 'invalid') return (
    <div className="force-light min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="signup-invalid">
        <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="text-red-500" size={28} />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">QR code expiré</h1>
        <p className="text-gray-500 text-sm">
          Ce QR code n'est plus valide. Demandez le QR code à jour à votre responsable d'agence.
        </p>
      </div>
    </div>
  );

  return (
    <div className="force-light min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <form onSubmit={submit} className="max-w-lg mx-auto" data-testid="agency-signup-form">
        <div className="text-center mb-6">
          <img src="/o-prime-logo.svg" alt="O Prime Energy" className="h-14 mx-auto mb-4" onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 className="text-2xl font-bold text-gray-900">Bienvenue dans l'équipe !</h1>
          <p className="text-gray-500 text-sm mt-1 inline-flex items-center gap-1.5 justify-center">
            <Building2 size={14} /> Agence <strong className="text-gray-700">{agence}</strong>
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
          <p className="text-xs text-gray-400">
            Première étape : vos coordonnées. Vous enchaînerez ensuite sur vos informations
            et vos documents (environ 5 minutes).
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Civilité <span className="text-red-400">*</span></label>
            <select value={form.civilite} onChange={set('civilite')} className={`${inputCls} bg-white`} data-testid="signup-civilite">
              <option value="">— Sélectionner —</option>
              <option value="Monsieur">Monsieur</option>
              <option value="Madame">Madame</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Prénom <span className="text-red-400">*</span></label>
              <input type="text" value={form.prenom} onChange={set('prenom')} className={inputCls} data-testid="signup-prenom" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom <span className="text-red-400">*</span></label>
              <input type="text" value={form.nom} onChange={set('nom')} className={inputCls} data-testid="signup-nom" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">E-mail <span className="text-red-400">*</span></label>
            <input type="email" value={form.email} onChange={set('email')} placeholder="prenom.nom@exemple.fr" className={inputCls} data-testid="signup-email" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Téléphone</label>
            <input type="tel" value={form.telephone} onChange={set('telephone')} placeholder="06 12 34 56 78" className={inputCls} data-testid="signup-telephone" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Type de contrat <span className="text-red-400">*</span></label>
            <select value={form.statut_contrat} onChange={set('statut_contrat')} className={`${inputCls} bg-white`} data-testid="signup-statut">
              <option value="">— Sélectionner —</option>
              {STATUT_OPTIONS.filter(o => !statuts || statuts.includes(o.value)).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Partenaire(s) avec qui vous allez travailler <span className="text-red-400">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {PARTNER_OPTIONS.map(p => {
                const on = form.partenaires.includes(p.value);
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => togglePartner(p.value)}
                    data-testid={`signup-partenaire-${p.value}`}
                    className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition
                      ${on ? 'bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200'
                           : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'}`}
                  >
                    {on ? '✓ ' : ''}{p.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700" data-testid="signup-error">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />{error}
          </div>
        )}

        <button
          type="submit"
          disabled={status === 'submitting'}
          data-testid="signup-submit"
          className="mt-5 w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 transition shadow-lg shadow-blue-200 disabled:opacity-60"
        >
          {status === 'submitting'
            ? <><Loader2 size={18} className="animate-spin" /> Création de votre dossier…</>
            : <><ChevronRight size={18} /> Continuer</>}
        </button>

        <p className="mt-4 text-center text-[11px] text-gray-400 inline-flex items-center gap-1 w-full justify-center">
          <CheckCircle size={12} /> Vos données sont transmises de façon sécurisée au service RH.
        </p>
      </form>
    </div>
  );
}
